import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Globe, Database, EnvelopeSimple, ArrowRight, CheckCircle, Clock, FileArrowDown } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

/**
 * /trust — public security & compliance posture page.
 *
 * Why this exists: corporate customers (property management companies,
 * mid-market contractors) route new SaaS through their IT / security team
 * before granting access. Apps with a public trust page get approved 5x
 * faster than apps that don't. The IT One-Pager PDF linked from Settings
 * refers back here for anyone doing due diligence.
 *
 * This is intentionally NOT a marketing page — plain, factual, clearly
 * dated so it looks credible to a corporate CISO / IT manager, not just
 * their end users.
 */
export default function Trust() {
  const lastUpdated = "February 24, 2026";
  return (
    <div className="min-h-screen bg-[#0F1F3A] text-white" data-testid="trust-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="trust-home-link">
            <div className="h-9 w-9 bg-white flex items-center justify-center rounded-md">
              <Logo size={32}/>
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none">Field Radar</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mt-0.5">Trust &amp; Security</div>
            </div>
          </Link>
          <Link to="/login" className="text-xs font-black uppercase tracking-widest text-white/70 hover:text-white transition"
                data-testid="trust-signin-link">
            Sign in →
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00C4CC]/15 border border-[#00C4CC]/40 text-[#7FE8ED] text-[10px] font-black uppercase tracking-widest">
            <ShieldCheck weight="fill" size={12}/> Last updated · {lastUpdated}
          </div>
          <h1 className="mt-5 font-display font-black text-5xl md:text-6xl leading-[1.05] tracking-tight">
            How we handle<br/>your data.
          </h1>
          <p className="mt-5 text-lg text-white/80 leading-relaxed font-medium">
            Field Radar is used by property management companies, contractors, and enterprise field crews to run daily operations. Here's the honest, current-state picture of our security posture — no marketing, no vague claims.
          </p>
        </div>

        {/* Grid */}
        <div className="mt-14 grid md:grid-cols-2 gap-4" data-testid="trust-grid">
          <Card icon={<Lock weight="fill" size={22}/>} title="Encryption">
            <li>TLS 1.3 in transit — every request, no exceptions</li>
            <li>AES-256 at rest on all databases and object storage</li>
            <li>Certificate pinning on the auth flow (SSL inspection carve-out required — see IT setup)</li>
            <li>Passwords hashed with bcrypt (cost factor 12)</li>
          </Card>

          <Card icon={<Database weight="fill" size={22}/>} title="Data Handling">
            <li>US-based hosting — data does not leave North America</li>
            <li>Per-tenant logical isolation — every query is filtered by <code className="font-mono text-[11px]">tenant_id</code></li>
            <li>No customer data used for AI model training</li>
            <li>Delete on request within 30 days, verifiable audit log</li>
          </Card>

          <Card icon={<Globe weight="fill" size={22}/>} title="Network">
            <li>HTTPS-only over TCP 443 · WebSocket upgrades allowed</li>
            <li>No inbound firewall requirements for customer networks</li>
            <li>Public IP allowlist available on request for enterprise customers</li>
            <li>Cloudflare in front — DDoS + WAF + global CDN</li>
          </Card>

          <Card icon={<CheckCircle weight="fill" size={22}/>} title="Compliance Roadmap">
            <li className="text-[#7FE8ED]"><b>SOC 2 Type 1</b> · in progress · target Q3 2026</li>
            <li>GDPR + CCPA-aligned data subject rights</li>
            <li>SAML SSO + SCIM · roadmap · Q4 2026</li>
            <li>HIPAA — not currently, not on roadmap. Do not upload PHI.</li>
          </Card>

          <Card icon={<Clock weight="fill" size={22}/>} title="Availability">
            <li>Target uptime · 99.9% (8.7h downtime/year)</li>
            <li>Automated backups every 6 hours · 30 day retention</li>
            <li>Public status page · <a href="https://status.fieldradar.pro" className="text-[#7FE8ED] hover:underline">status.fieldradar.pro</a></li>
            <li>Incident postmortems published within 5 business days</li>
          </Card>

          <Card icon={<ShieldCheck weight="fill" size={22}/>} title="Access &amp; Auth">
            <li>Role-based access — admin / office / worker / read-only</li>
            <li>Multi-tenant row-level filtering on every query</li>
            <li>Session tokens rotated on password change / device change</li>
            <li>Push notification tokens per device, revocable individually</li>
          </Card>
        </div>

        {/* Corporate IT section */}
        <section className="mt-16 rounded-2xl bg-white/5 border border-white/10 p-8" data-testid="trust-it-section">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-lg bg-[#00C4CC]/20 border border-[#00C4CC]/40 flex items-center justify-center flex-shrink-0">
              <FileArrowDown weight="fill" size={22} className="text-[#7FE8ED]"/>
            </div>
            <div className="flex-1 min-w-[280px]">
              <h2 className="font-display font-black text-2xl">For corporate IT teams</h2>
              <p className="mt-2 text-white/70 leading-relaxed">
                If your office firewall or web filter is blocking Field Radar, hand your IT team the one-pager below. It contains the exact hostnames, protocol requirements, filter-vendor submission links, and SSL-inspection carve-out language they need.
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs font-black uppercase tracking-widest">
                <a href="mailto:security@fieldradar.pro"
                   data-testid="trust-security-email"
                   className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] transition">
                  <EnvelopeSimple weight="fill" size={14}/> security@fieldradar.pro
                </a>
                <Link to="/login"
                   className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-white/20 hover:bg-white/10 transition">
                  Sign in → Settings → Office Network <ArrowRight size={12}/>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="mt-12 border-t border-white/10 pt-10">
          <h2 className="font-display font-black text-2xl">Report a vulnerability</h2>
          <p className="mt-2 text-white/70 max-w-2xl leading-relaxed">
            Found a security issue? Email <a href="mailto:security@fieldradar.pro" className="text-[#7FE8ED] hover:underline">security@fieldradar.pro</a> with reproduction steps. We acknowledge within 24 hours and do not pursue legal action against good-faith researchers who give us reasonable time to fix.
          </p>
        </section>

        <footer className="mt-16 flex flex-wrap gap-4 items-center justify-between text-xs text-white/50">
          <div>© {new Date().getFullYear()} Field Radar · <a href="mailto:security@fieldradar.pro" className="hover:text-white/80">security@fieldradar.pro</a></div>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-white/80">Home</Link>
            <Link to="/login" className="hover:text-white/80">Sign in</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-6 hover:bg-white/[0.07] transition"
         data-testid={`trust-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#00C4CC]/15 border border-[#00C4CC]/30 flex items-center justify-center text-[#7FE8ED]">
          {icon}
        </div>
        <h3 className="font-display font-black text-lg tracking-tight">{title}</h3>
      </div>
      <ul className="mt-4 space-y-2 text-sm text-white/80 leading-relaxed list-disc ml-5">
        {children}
      </ul>
    </div>
  );
}
